
import React, { useState, useEffect } from "react";
import { Eye, EyeOff, AlertCircle, Loader, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";
import { showSuccessToast, showErrorToast } from "@/utils/toastUtils";
import { debugLogger } from '@/utils/debugLogger';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { TermsModal } from './TermsModal';
import { CommunityGuidelinesModal } from './CommunityGuidelinesModal';
import { PrivacyModal } from './PrivacyModal';
import { useAuditLog } from '@/hooks/useAuditLog';

type FormMode = "login" | "register" | "forgot-password";

interface PasswordCriteria {
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  isLongEnough: boolean;
}

interface AuthFormProps {
  onLearnMore: () => void;
}

export const AuthForm: React.FC<AuthFormProps> = ({ onLearnMore }) => {
  const { logEvent } = useAuditLog();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>("login");
  const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | null>(null);
  const [passwordCriteria, setPasswordCriteria] = useState<PasswordCriteria>({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecial: false,
    isLongEnough: false
  });
  const [error, setError] = useState<string | null>(null);
  const [shakeFields, setShakeFields] = useState({
    email: false,
    password: false,
    confirmPassword: false,
    terms: false,
    guidelines: false,
    privacy: false
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    if (formMode === "register" && password) {
      const hasLowercase = /[a-z]/.test(password);
      const hasUppercase = /[A-Z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecial = /[^A-Za-z0-9]/.test(password);
      const isLongEnough = password.length >= 8;
      
      setPasswordCriteria({
        hasLowercase,
        hasUppercase,
        hasNumber,
        hasSpecial,
        isLongEnough
      });
      
      const strengthScore = [hasLowercase, hasUppercase, hasNumber, hasSpecial, isLongEnough].filter(Boolean).length;
      
      if (strengthScore <= 2) setPasswordStrength("weak");
      else if (strengthScore <= 4) setPasswordStrength("medium");
      else setPasswordStrength("strong");
    } else {
      setPasswordStrength(null);
      setPasswordCriteria({
        hasLowercase: false,
        hasUppercase: false,
        hasNumber: false,
        hasSpecial: false,
        isLongEnough: false
      });
    }
  }, [password, formMode]);

  useEffect(() => {
    setError(null);
  }, [formMode]);

  useEffect(() => {
    const hasAnyShake = Object.values(shakeFields).some(shake => shake);
    if (hasAnyShake) {
      const timer = setTimeout(() => {
        setShakeFields({
          email: false,
          password: false,
          confirmPassword: false,
          terms: false,
          guidelines: false,
          privacy: false
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shakeFields]);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid = () => {
    const newShakeFields = {
      email: false,
      password: false,
      confirmPassword: false,
      terms: false,
      guidelines: false,
      privacy: false
    };

    let hasErrors = false;
    let priorityError = "";

    // 1. Email validation (highest priority)
    if (!email) {
      priorityError = "Por favor, preencha o email";
      newShakeFields.email = true;
      hasErrors = true;
    } else if (!isValidEmail(email)) {
      priorityError = "Por favor, informe um email válido";
      newShakeFields.email = true;
      hasErrors = true;
    }
    
    // 2. Password validation
    if (formMode !== "forgot-password" && !password) {
      if (!priorityError) priorityError = "Por favor, preencha a senha";
      newShakeFields.password = true;
      if (formMode === "register") {
        newShakeFields.confirmPassword = true;
      }
      hasErrors = true;
    }
    
    if (formMode === "register") {
      // Check password strength
      if (password && passwordStrength === "weak") {
        if (!priorityError) priorityError = "Sua senha é muito fraca. Use letras maiúsculas, minúsculas, números e caracteres especiais.";
        newShakeFields.password = true;
        hasErrors = true;
      }

      // 3. Confirm password validation
      if (!confirmPassword) {
        if (!priorityError) priorityError = "Por favor, preencha a confirmação da senha";
        newShakeFields.confirmPassword = true;
        hasErrors = true;
      } else if (password !== confirmPassword) {
        if (!priorityError) priorityError = "As senhas não coincidem";
        newShakeFields.password = true;
        newShakeFields.confirmPassword = true;
        hasErrors = true;
      }

      // 4. Terms validation
      if (!termsAccepted) {
        if (!priorityError) priorityError = "Você deve concordar com os Termos de Uso para criar uma conta.";
        newShakeFields.terms = true;
        hasErrors = true;
      }

      // 5. Guidelines validation
      if (!guidelinesAccepted) {
        if (!priorityError) priorityError = "Você deve concordar com as Diretrizes da Comunidade para criar uma conta.";
        newShakeFields.guidelines = true;
        hasErrors = true;
      }

      // 6. Privacy Policy validation
      if (!privacyAccepted) {
        if (!priorityError) priorityError = "Você deve concordar com a Política de Privacidade para criar uma conta.";
        newShakeFields.privacy = true;
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setError(priorityError);
      setShakeFields(newShakeFields);
    }

    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isFormValid()) {
      return;
    }
    setLoading(true);
    try {
      switch (formMode) {
        case "login":
          debugLogger.debug('AuthForm: Attempting login');
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password
          });
          if (signInError) throw signInError;
          
          if (signInData?.user) {
            debugLogger.debug('auth', 'Login successful');
            
            // Log login event to audit table
            await logEvent({
              event_type: 'login',
              user_id: signInData.user.id,
              session_id: signInData.session?.access_token,
            });
          }
          break;
          
        case "register":
          debugLogger.debug('AuthForm: Attempting registration');
          const username = "user_" + email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
          
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: username,
              }
            }
          });
          
          if (signUpError) throw signUpError;
          
          if (signUpData?.user) {
            debugLogger.info('AuthForm: Registration successful');
          }
          break;
      }
    } catch (error: any) {
      // Invalid credentials is expected user error, not app error
      const isInvalidCredentials = error.message?.includes("Invalid login");
      if (isInvalidCredentials) {
        debugLogger.warn('AuthForm', 'Invalid login attempt', { email: email.substring(0, 3) + '***' });
      } else {
        debugLogger.error('AuthForm', 'Auth error:', error);
      }
      let errorMsg = "Ocorreu um erro. Tente novamente mais tarde.";
      if (error.message) {
        if (error.message.includes("Invalid login")) {
          errorMsg = "Email ou senha incorretos.";
        } else if (
          error.message.includes("already registered") || 
          error.message.includes("already been registered") ||
          error.message.includes("User already exists") ||
          error.message.toLowerCase().includes("email already")
        ) {
          errorMsg = "Este email já possui uma conta. Faça login ou recupere sua senha.";
        } else if (error.message.includes("rate limited")) {
          errorMsg = "Muitas tentativas. Tente novamente mais tarde.";
        } else if (
          error.message.includes("Password is known to be weak") ||
          error.message.includes("weak and easy to guess") ||
          error.message.toLowerCase().includes("hibp") ||
          error.message.toLowerCase().includes("pwned")
        ) {
          errorMsg = "Esta senha foi exposta em vazamentos de dados conhecidos. Por segurança, escolha uma senha diferente.";
        } else {
          errorMsg = error.message;
        }
      }
      setError(errorMsg);
      setShakeFields(prev => ({ ...prev, password: true }));
      
      showErrorToast({
        title: "Erro no " + (formMode === "login" ? "login" : "registro"),
        description: errorMsg
      });
    } finally {
      setLoading(false);
    }
  };

  const renderLabels = () => {
    switch (formMode) {
      case "login":
        return "Entrar";
      case "register":
        return "Criar Conta";
      case "forgot-password":
        return "Recuperar Senha";
    }
  };

  if (formMode === "forgot-password") {
    return <ForgotPasswordForm onBackToLogin={() => setFormMode("login")} />;
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-1">
          <Label htmlFor="email" className="text-white">
            Email
          </Label>
          <div className="relative">
            <Input 
              id="email" 
              type="email" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              className={cn(
                "bg-white/10 border-white/20 !text-white placeholder:text-white/50",
                "[&:-webkit-autofill]:!text-white [&:-webkit-autofill]:[-webkit-text-fill-color:white] [&:-webkit-autofill]:shadow-[0_0_0_1000px_rgba(255,255,255,0.1)_inset]",
                shakeFields.email && "animate-[shake_0.5s_ease-in-out]"
              )} 
              placeholder="seu@email.com" 
            />
            {email && !isValidEmail(email) && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <AlertCircle className="h-5 w-5 text-red-500" />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="password" className="text-white">
            Senha
          </Label>
          <div className="relative">
            <Input 
              id="password" 
              type={showPassword ? "text" : "password"} 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              autoComplete={formMode === "login" ? "current-password" : "new-password"}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className={cn(
                "bg-white/10 border-white/20 !text-white pr-10 placeholder:text-white/50",
                "[&:-webkit-autofill]:!text-white [&:-webkit-autofill]:[-webkit-text-fill-color:white] [&:-webkit-autofill]:shadow-[0_0_0_1000px_rgba(255,255,255,0.1)_inset]",
                shakeFields.password && "animate-[shake_0.5s_ease-in-out]"
              )} 
              placeholder="Sua senha" 
            />
            <button 
              type="button" 
              onClick={() => setShowPassword(!showPassword)} 
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:text-white/80 transition-colors"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          {formMode === "register" && (
            <PasswordStrengthIndicator 
              password={password}
              criteria={passwordCriteria}
              strength={passwordStrength}
            />
          )}
        </div>

        {formMode === "register" && (
          <div className="space-y-1">
            <Label htmlFor="confirmPassword" className="text-white">
              Confirme sua senha
            </Label>
            <div className="relative">
              <Input 
                id="confirmPassword" 
                type={showPassword ? "text" : "password"} 
                value={confirmPassword} 
                onChange={e => setConfirmPassword(e.target.value)} 
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className={cn(
                  "bg-white/10 border-white/20 !text-white pr-16 placeholder:text-white/50",
                  "[&:-webkit-autofill]:!text-white [&:-webkit-autofill]:[-webkit-text-fill-color:white] [&:-webkit-autofill]:shadow-[0_0_0_1000px_rgba(255,255,255,0.1)_inset]",
                  shakeFields.confirmPassword && "animate-[shake_0.5s_ease-in-out]"
                )} 
                placeholder="Repita sua senha" 
              />
              {confirmPassword && password !== confirmPassword && (
                <div className="absolute right-10 top-1/2 -translate-y-1/2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
              )}
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:text-white/80 transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-400 mt-1">As senhas não coincidem</p>
            )}
          </div>
        )}

        {formMode === "register" && (
          <div className="space-y-3">
            <div className={cn("flex items-start space-x-3", shakeFields.terms && "animate-[shake_0.5s_ease-in-out]")}>
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                className="mt-1 border-white/40 data-[state=checked]:border-luxury-red data-[state=checked]:bg-luxury-red"
              />
              <Label htmlFor="terms" className="text-sm text-white/90 leading-relaxed cursor-pointer">
                Concordo com os{" "}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                >
                  Termos de Uso
                </button>
              </Label>
            </div>
            
            <div className={cn("flex items-start space-x-3", shakeFields.guidelines && "animate-[shake_0.5s_ease-in-out]")}>
              <Checkbox
                id="guidelines"
                checked={guidelinesAccepted}
                onCheckedChange={(checked) => setGuidelinesAccepted(checked as boolean)}
                className="mt-1 border-white/40 data-[state=checked]:border-luxury-red data-[state=checked]:bg-luxury-red"
              />
              <Label htmlFor="guidelines" className="text-sm text-white/90 leading-relaxed cursor-pointer">
                Concordo com as{" "}
                <button
                  type="button"
                  onClick={() => setShowGuidelinesModal(true)}
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                >
                  Diretrizes da Comunidade
                </button>
              </Label>
            </div>

            <div className={cn("flex items-start space-x-3", shakeFields.privacy && "animate-[shake_0.5s_ease-in-out]")}>
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={(checked) => setPrivacyAccepted(checked as boolean)}
                className="mt-1 border-white/40 data-[state=checked]:border-luxury-red data-[state=checked]:bg-luxury-red"
              />
              <Label htmlFor="privacy" className="text-sm text-white/90 leading-relaxed cursor-pointer">
                Concordo com a{" "}
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                >
                  Política de Privacidade
                </button>
              </Label>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-500 text-sm py-2 px-3 bg-red-500/10 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button type="submit" variant="luxury" className="w-full" disabled={loading}>
          {loading ? (
            <div className="flex items-center justify-center">
              <Loader className="animate-spin mr-2 h-4 w-4" />
              <span>Processando...</span>
            </div>
          ) : (
            renderLabels()
          )}
        </Button>
      </form>

      <div className="mt-6 pt-4 border-t border-white/10">
        <div className="text-center space-y-3">
          {formMode === "login" && (
            <>
              <Button
                type="button"
                onClick={() => {
                  setPassword("");
                  setConfirmPassword("");
                  setFormMode("register");
                }}
                variant="light"
                className="w-full"
              >
                Cadastre-se
              </Button>
              <Button 
                type="button"
                variant="link"
                onClick={() => {
                  setPassword("");
                  setFormMode("forgot-password");
                }} 
                className="w-full text-sm text-white/60 hover:text-white/90"
              >
                Esqueceu sua senha?
              </Button>
            </>
          )}

          {formMode === "register" && (
            <Button
              type="button"
              onClick={() => {
                setPassword("");
                setConfirmPassword("");
                setFormMode("login");
              }}
              variant="luxury"
              className="w-full"
            >
              Já tem uma conta? Faça login
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-white/10">
        <Button
          type="button"
          variant="ghost-dark"
          onClick={onLearnMore}
          className="w-full transition-all duration-300 group"
        >
          <span className="mr-2">Saiba mais sobre o Interlaced</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </div>

      <TermsModal open={showTermsModal} onOpenChange={setShowTermsModal} />
      <CommunityGuidelinesModal open={showGuidelinesModal} onOpenChange={setShowGuidelinesModal} />
      <PrivacyModal open={showPrivacyModal} onOpenChange={setShowPrivacyModal} />
    </>
  );
};

export default AuthForm;
